import { useRef, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type CLRM, mutationFunctions } from "@/models/clrm.tsx";
import { useLeaData } from "@/queries.ts";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import Popup from "@/components/shared/Popup";
import { CLRMAddEditForm } from "@/components/records/classroom/CLRMAddEditForm";
import { dateToISOString } from "@/utils/util";
import { FiEdit, FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";

const columns: GridColumnProps[] = [
  {
    id: "ProgramYear",
    field: "ProgramYear",
    title: "Program Year",
  },
  {
    id: "PreschoolName",
    field: "PreschoolName",
    title: "School",
  },
  {
    id: "ClassroomName",
    field: "ClassroomName",
    title: "Classroom Name",
  },
  {
    id: "LocalClassroomId",
    field: "LocalClassroomId",
    title: "Classroom ID",
  },
  {
    id: "EffectiveStartDate",
    field: "EffectiveStartDate",
    title: "Effective Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Classroom Name", key: "ClassroomName" },
  { label: "Classroom ID", key: "LocalClassroomId" },
  { label: "Effective Start Date", key: "EffectiveStartDate" },
];

export default function CLRMRecordsTab({
  clrmRecords,
}: {
  clrmRecords: CLRM[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupClassroom, setPopupClassroom] = useState<CLRM | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: CLRM;
    dateChanged: boolean;
  } | null>(null);
  const pendingDeleteNameRef = useRef<string>("");
  const shouldNavigateAfterDeleteRef = useRef<boolean>(false);

  const addEditForm = useForm<CLRM>({
    defaultValues: { EffectiveStartDate: dateToISOString(new Date()) },
  });

  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const closePopup = () => {
    setPopupOpen(false);
    setPopupClassroom(undefined);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setSubmitConfirm(null);
    addEditForm.reset({});
    addEditForm.clearErrors();
  };

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "CLRM",
      toastMessage: "Classroom record submitted.\nWaiting for validation...",
      onSuccess: closePopup,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "CLRM",
    toastMessage: () =>
      `${pendingDeleteNameRef.current} classroom record submitted for deletion`,
    successMessage: () =>
      `${pendingDeleteNameRef.current} classroom record deleted successfully`,
    onSuccess: () => {
      closePopup();
      if (shouldNavigateAfterDeleteRef.current) {
        navigate({
          to: "/$leaid/records/classroom",
          params: { leaid: leaContext.currentUserOrg.esiIdPath },
        });
      }
    },
  });

  const mutateFns = mutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = mutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<CLRM> = (x) => {
    if (!popupClassroom) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }

    const dateChanged =
      (popupClassroom.EffectiveStartDate ?? "") !==
      (x.EffectiveStartDate ?? "");

    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const handleCancelSubmit = () => {
    setSubmitConfirm(null);
  };

  const onDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (popupClassroom) {
      const localClassroomId = popupClassroom.LocalClassroomId;
      const clrmCountForId = clrmRecords.filter(
        (r) => r.LocalClassroomId === localClassroomId
      ).length;

      pendingDeleteNameRef.current = popupClassroom.ClassroomName || "";
      shouldNavigateAfterDeleteRef.current = clrmCountForId === 1;
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(popupClassroom, true);
    }
  };

  const onAddButtonClick = () => {
    setPopupOpen(true);
    setPopupClassroom(undefined);
    addEditForm.reset({ EffectiveStartDate: dateToISOString(new Date()) });
    addEditForm.clearErrors();
  };

  const onEditButtonClick = (c: CLRM) => {
    setPopupOpen(true);
    setPopupClassroom(c);
    addEditForm.reset(c);
    addEditForm.clearErrors();
  };

  const formId = "frmCLRM";
  const canEdit = leaContext.hasRole("CLRMEdit");

  const tableData = clrmRecords.map((record) => ({
    ProgramYear: record.ProgramYear,
    PreschoolName: leaContext?.currentOrgPreschools?.find(
      (x) => x.PreschoolCDSCode === record.PreschoolCode
    )?.PreschoolName,
    ClassroomName: record.ClassroomName,
    LocalClassroomId: record.LocalClassroomId,
    EffectiveStartDate: record.EffectiveStartDate
      ? formatDateOnly(record.EffectiveStartDate)
      : "",
    __raw: record,
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export classroom records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["ClassroomName"],
              "classroom-record"
            )}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a classroom record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>
      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) => {
          onEditButtonClick(event.dataItem.__raw);
        }}
      />
      <Popup
        show={popupOpen}
        enforceFocus={!showDeleteConfirm && !submitConfirm}
        header={
          popupClassroom == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Add Classroom Record" : "View Classroom Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Edit Classroom Record" : "View Classroom Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          popupClassroom ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText="Cancel"
        size="lg"
        onClose={closePopup}
        onSecondButtonClick={closePopup}
        canEdit={canEdit}
        leftFooterButton={
          canEdit && popupClassroom ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={onDeleteClick}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form
          id={formId}
          onSubmit={addEditForm.handleSubmit(onSubmit, () =>
            focusFirstInvalidField(`#${formId}`)
          )}
        >
          <CLRMAddEditForm
            selectedClassroom={popupClassroom}
            form={addEditForm}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={handleCancelSubmit}
          onConfirm={handleConfirmSubmit}
        />
      )}
    </>
  );
}
